// The browser fillings for every port in `packages/client/src/ports.ts`
// (#119), and the one file in this client that is allowed to reach for the
// environment.
//
// `@cardsalot/client` references no `WebSocket`, no `setTimeout`, no
// `localStorage` and no clock; each arrives from here. The app's
// `platform/ports.ts` is the same file for React Native, and the comment on
// its `secureTokenStore` already named this one: "a web host wraps
// `localStorage` instead".
//
// This is also the D13 exemption `eslint.config.js` describes. ESLint can
// only ignore a directory, so the rule is stated here instead: `Date.now`,
// `crypto.getRandomValues`, `setTimeout` and `localStorage` appear in THIS
// module and nowhere else in `web/src`. Everything above it takes them as
// arguments, which is what keeps the flow testable without a browser.
import type {
  AppLifecycle,
  AppVisibility,
  Scheduler,
  SocketFactory,
  TokenStore,
} from "@cardsalot/client";

/**
 * The browser's `WebSocket`.
 *
 * `event.code` and `event.reason` go through untouched, for the reason the
 * app's factory states: the reason is what separates "dropped, dial again"
 * from "refused, and it will be refused again"
 * (`isTerminalCloseReason`), and a factory that normalised it away would
 * turn a removal into a redial loop.
 *
 * `send` checks `readyState` first. A browser throws `InvalidStateError` on
 * a socket that is closing, and the one caller is the heartbeat, which is on
 * a timer and can fire into a socket the tab suspended a moment ago.
 */
export const browserSocketFactory: SocketFactory = (url, handlers) => {
  const socket = new WebSocket(url);
  socket.addEventListener("open", () => handlers.onOpen());
  socket.addEventListener("message", (event: MessageEvent) => {
    handlers.onMessage(String(event.data));
  });
  socket.addEventListener("close", (event: CloseEvent) => {
    handlers.onClose(event.code, event.reason);
  });
  socket.addEventListener("error", (event: Event) => handlers.onError(event));
  return {
    send: (data) => {
      if (socket.readyState === WebSocket.OPEN) socket.send(data);
    },
    close: () => socket.close(),
  };
};

/**
 * The Accounts service's origin (D17), baked in at build time.
 *
 * The one thing in this client that is NOT same-origin. Everything else — the
 * pages, `/ws`, `/invites/:code` — is served by the Title's own Worker, but
 * sign-in lives on ONE origin shared by every Title, so this cannot be
 * derived from `location`.
 *
 * `http://127.0.0.1:8788` is the default because that is the port
 * `services/accounts/wrangler.jsonc` pins for `wrangler dev`, so a dev build
 * with no `.env` reaches a local Accounts. Cookies ignore the port, so a
 * cookie set there is sent from the Title's own port without any cross-site
 * ceremony — which is exactly why `COOKIE_DOMAIN` is left unset locally.
 */
export const accountsBase = (): string =>
  import.meta.env.VITE_ACCOUNTS_BASE ?? "http://127.0.0.1:8788";

export const browserScheduler: Scheduler = {
  setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
  clearTimeout: (handle) =>
    globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/** The clock and the randomness the CLIENT uses — not a Rules module's. D13
 *  is about code the Engine re-runs after an eviction; this is the host. */
export const now = (): number => Date.now();

/** 32 bits of real entropy in [0, 1). `Math.random` would do for a backoff
 *  jitter, but the same function mints this browser's player id, and a
 *  predictable one is a guessable identity. */
export const random = (): number => {
  const word = globalThis.crypto.getRandomValues(new Uint32Array(1))[0] ?? 0;
  return word / 2 ** 32;
};

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

/** The three methods this client uses of `Storage`. Narrow so a test can
 *  pass a `Map` and so the failure modes below are the only ones. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * `localStorage`, or `null` when there is none.
 *
 * Every access is guarded, and not out of caution: reading `localStorage` at
 * all THROWS in a Chromium window with third-party storage blocked, and
 * `setItem` throws in a Safari private window and whenever the quota is
 * full. A client that let either escape would fail to open a Match because
 * it could not remember a name — so "no storage" degrades to "nothing
 * remembered", which is exactly what a fresh browser is.
 */
export const safeStorage = (): StorageLike | null => {
  try {
    const store = globalThis.localStorage;
    // Touching a key is the probe: the throw happens on access, not on the
    // property lookup.
    store.getItem("nickels.probe");
    return store;
  } catch {
    return null;
  }
};

/** A `StorageLike` that never throws and remembers nothing when there is no
 *  storage behind it. */
export const guardedStorage = (
  store: StorageLike | null = safeStorage(),
): StorageLike => ({
  getItem: (key) => {
    try {
      return store?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      store?.setItem(key, value);
    } catch {
      // A full or blocked store loses the seat on reload; it must not lose
      // the Match that is open right now.
    }
  },
  removeItem: (key) => {
    try {
      store?.removeItem(key);
    } catch {
      // As above.
    }
  },
});

/**
 * Session tokens in `localStorage`, keyed exactly as the client keys them
 * (`<titleId>:<matchId>`) under one prefix.
 *
 * The port is async because the native side is `expo-secure-store`; here the
 * values are already resolved. `localStorage` is deliberate over
 * `sessionStorage`: the token is what makes a CLOSED tab reopen into the
 * same Seat, and `sessionStorage` dies with the tab.
 */
export const TOKEN_PREFIX = "nickels.token.";

export const localTokenStore = (
  store: StorageLike = guardedStorage(),
): TokenStore => ({
  read: async (key) => store.getItem(`${TOKEN_PREFIX}${key}`),
  write: async (key, token) => {
    store.setItem(`${TOKEN_PREFIX}${key}`, token);
  },
  clear: async (key) => {
    store.removeItem(`${TOKEN_PREFIX}${key}`);
  },
});

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

export const visibilityOf = (state: string): AppVisibility =>
  state === "visible" ? "foreground" : "background";

/**
 * `visibilitychange` → the client's foreground/background port.
 *
 * The app's `appStateLifecycle` says why the port exists at all: a suspended
 * app's socket dies with **no close event**, so a client without this comes
 * back believing it is connected and writes every frame into a dead pipe. A
 * phone locked with a browser tab open is the same story — iOS freezes the
 * page, the socket goes, and nothing on it ever says so.
 */
export const visibilityLifecycle = (
  doc: Pick<
    Document,
    "visibilityState" | "addEventListener" | "removeEventListener"
  > = document,
): AppLifecycle => ({
  subscribe: (listener) => {
    const handler = () => listener(visibilityOf(doc.visibilityState));
    doc.addEventListener("visibilitychange", handler);
    return () => doc.removeEventListener("visibilitychange", handler);
  },
});

/**
 * A repeating timer, as a start that returns its own cancel.
 *
 * `pollStatus` needs one and this module is where a timer is allowed to come
 * from; handing back the cancel rather than a handle keeps the opaque
 * `setInterval` return inside this file.
 */
export const everyMs = (fn: () => void, ms: number): (() => void) => {
  const handle = globalThis.setInterval(fn, ms);
  return () => globalThis.clearInterval(handle);
};

/**
 * Run `fn` once the document has finished loading and one frame has painted.
 *
 * The live regions need it (#190). VoiceOver announces the new document
 * ("web content area, to enter…") for a moment after a navigation, and a
 * `polite` message written during that window is dropped — measured, the
 * join flow's `Found Joan's game` landed **12 ms before** `load` and was
 * never spoken. Waiting for `load` puts the first sentence after the
 * document's own announcement rather than under it.
 *
 * The frame is the second half and is not decoration: the regions are
 * appended and filled in the same task (`pages/join.ts` appends, then starts
 * the flow), and a region whose text was already there when the reader saw
 * the element announces nothing, because there was no CHANGE. One painted
 * frame with them empty is what makes the text a change.
 *
 * `readyState === "complete"` is checked first because `load` has no replay:
 * a listener added after it fired would wait for ever.
 */
export const afterLoad = (fn: () => void): void => {
  const nextFrame = () => {
    const raf = globalThis.requestAnimationFrame;
    if (typeof raf === "function") raf(() => fn());
    else fn();
  };
  if (globalThis.document?.readyState === "complete") nextFrame();
  else globalThis.addEventListener("load", nextFrame, { once: true });
};
